import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { api, endpoints } from '@/lib/api';
import { authStateQueryKey } from '@/lib/hooks/use-auth';
import { wrapCreateRootRouteWithSentry } from '@sentry/tanstackstart-react';
import type { QueryClient } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router';
// import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { createServerFn } from '@tanstack/react-start';
import { getRequestHeader } from '@tanstack/react-start/server';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import appStyles from '../styles/main.css?url';

const getUser = createServerFn().handler(async () => {
  const cookie = getRequestHeader('Cookie');

  try {
    const { data } = await api.get(endpoints.auth.verify, {
      headers: { cookie },
    });
    return data;
  } catch {
    return null;
  }
});

export const Route = wrapCreateRootRouteWithSentry(createRootRouteWithContext)<{
  queryClient: QueryClient;
  user: { id: string } | undefined;
}>()({
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.fetchQuery({
      queryKey: authStateQueryKey,
      queryFn: ({ signal }) => getUser({ signal }),
    });
    return { user };
  },
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Formalearning - Validation des jeux de Question-Réponse',
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap',
      },
      {
        rel: 'stylesheet',
        href: appStyles,
      },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '96x96',
        href: '/favicon-96x96.png',
      },
      { rel: 'manifest', href: '/site.webmanifest', color: '#fffff' },
      { rel: 'icon', href: '/favicon.ico' },
    ],
  }),
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <TooltipProvider>
        <Outlet />
        <Toaster />
      </TooltipProvider>
    </RootDocument>
  );
}

function RootDocument({ children }: { children: React.ReactNode }) {
  const [isConsideredLoaded, setIsConsideredLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsConsideredLoaded(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  return (
    <html lang="en" data-lt-installed suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body
        className={'antialiased'}
        style={{ visibility: isConsideredLoaded ? 'visible' : 'hidden' }}
      >
        {/* <ReactQueryDevtools buttonPosition="bottom-left" />
        <TanStackRouterDevtools position="bottom-right" /> */}

        {children}
        <Scripts />
      </body>
    </html>
  );
}
